import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { BoardContent } from "../../types/app/boardContent.types";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { dictionary } from "../../utils/core/dictionary";
import "./BoardContent.scss";
import { apiUrl } from "../../api/core/apiUrl";
import http from "../../api/core/api";
import {
  DragEvent,
  Fragment,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import { Board } from "../../types/app/board.types";
import { theme } from "../../App";
import { fullscreenPathPrefix } from "../../utils/core/routes";
import { useLocation } from "react-router-dom";
import { localStorageKeys } from "../../utils/keys/localStorageKeys";
import { CustomDialog } from "../../components/core/Dialogs/CustomDialog";
import { BoardContentCardFilePreview } from "./BoardContentCardFilePreview";

interface BoardContentAttachment {
  id: number;
  parentId: number;
  name: string;
  contentType: string;
  sizeInBytes: number;
}

interface Props {
  readOnly: boolean;
  boardContent: BoardContent;
  board: Board;
  attachments?: BoardContentAttachment[];
  handleOnDelete: (id: number) => void;
  handleOnEdit: (boardContent: BoardContent) => void;
  handleOnCreate: (boardContent: BoardContent) => void;
  handleOnDragStart?: (boardContent: BoardContent) => void;
  handleOnDragEnd?: () => void;
}

const FILE_CACHE_MS = 15 * 60 * 1000;

const authHeaders = () => {
  const token = localStorage.getItem(localStorageKeys.BEARER_TOKEN_KEY);
  if (token) {
    return { Authorization: `Bearer ${JSON.parse(token)}` };
  }
  return null;
};

export function BoardContentCard({
  readOnly,
  boardContent,
  board,
  attachments,
  handleOnDelete,
  handleOnEdit,
  handleOnCreate,
  handleOnDragStart,
  handleOnDragEnd,
}: Props) {
  const { pathname } = useLocation();
  const isFullScreen = pathname.includes(fullscreenPathPrefix);
  const placeholder = require("../../placeholder.png");
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [attachmentHeight, setAttachmentHeight] = useState<number>(100);
  const [iframeState, setIframeState] = useState<"loading" | "loaded" | "failed">(
    "loading"
  );
  const [iframeReloadKey, setIframeReloadKey] = useState<number>(0);

  const attachmentId = useMemo(
    () => (attachments && attachments.length > 0 ? attachments[0].id : null),
    [attachments]
  );

  const urlDetail = boardContent.urlDetail;
  const previewQueryEnabled = !!urlDetail && !attachmentId;

  const { data: isPreviewSupported } = useQuery<boolean>(
    ["network-file-supported", urlDetail],
    async () => {
      const res = await http.apiGetFiles({
        url: `${apiUrl.BOARD_CONTENT_NETWORK_FILE_IS_PREVIEW}?path=${encodeURIComponent(
          urlDetail ?? ""
        )}`,
        config: {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          method: "GET",
        },
      });
      return !!res?.data;
    },
    {
      enabled: previewQueryEnabled,
      staleTime: FILE_CACHE_MS,
    }
  );

  // Táhne se za úchyt, ale jako náhled tažení se zobrazí celá karta
  const handleDragStart = (e: DragEvent<HTMLSpanElement>) => {
    e.dataTransfer.effectAllowed = "move";
    // Firefox bez nastavených dat tažení nespustí
    e.dataTransfer.setData("text/plain", String(boardContent.id));
    if (cardRef.current) e.dataTransfer.setDragImage(cardRef.current, 20, 15);
    handleOnDragStart?.(boardContent);
  };

  const getAction = () => {
    if (readOnly) return undefined;
    return (
      <Grid sx={{ display: "flex", alignItems: "center" }}>
        {boardContent.id !== null && (
          <>
            {handleOnDragStart && (
              <Tooltip title={dictionary.DRAG_TO_MOVE}>
                <span
                  className={"drag-handle"}
                  draggable
                  onDragStart={handleDragStart}
                  onDragEnd={() => handleOnDragEnd?.()}
                >
                  <DragIndicatorIcon className={"icon-button"} />
                </span>
              </Tooltip>
            )}
            <Tooltip title={dictionary.EDIT}>
              <EditIcon
                className={"icon-button"}
                onClick={() => boardContent.id && handleOnEdit(boardContent)}
              />
            </Tooltip>
            <Tooltip title={dictionary.DELETE}>
              <DeleteIcon
                className={"icon-button"}
                onClick={() =>
                  boardContent.id && handleOnDelete(boardContent.id)
                }
              />
            </Tooltip>
          </>
        )}
        {boardContent.id === null && (
          <Tooltip title={dictionary.ADD}>
            <AddIcon
              className={"icon-button"}
              onClick={() => handleOnCreate(boardContent)}
            />
          </Tooltip>
        )}
      </Grid>
    );
  };

  // Zjistit velikost karty, odečíst velikost hlavičky
  useEffect(() => {
    let item = document.getElementsByClassName("MuiCard-root");
    let height = item?.item(0)?.clientHeight;
    setAttachmentHeight(height ? height - 30 : 150);
  }, []);

  // Timeout pro detekci selhaného načtení iframu (např. X-Frame-Options nebo SSO redirect)
  useEffect(() => {
    if (!boardContent.urlPreview || attachmentId) return;
    setIframeState("loading");
    const timer = window.setTimeout(() => {
      setIframeState((prev) => (prev === "loaded" ? prev : "failed"));
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [boardContent.urlPreview, iframeReloadKey, attachmentId]);

  const handleIframeLoad = (e: SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const href = e.currentTarget.contentWindow?.location.href;
      if (href === "about:blank") {
        setIframeState("failed");
        return;
      }
    } catch {
      // cross-origin přístup vyhodí chybu — to znamená, že se cílová stránka načetla
    }
    setIframeState("loaded");
  };

  const openIframeInNewTab = () => {
    if (boardContent.urlPreview) {
      window.open(boardContent.urlPreview, "_blank", "noopener,noreferrer");
    }
  };

  const prewarmSso = () => {
    if (!boardContent.urlPreview) return;
    try {
      const u = new URL(boardContent.urlPreview);
      window.open(u.origin, "_blank", "noopener,noreferrer");
    } catch {
      window.open(boardContent.urlPreview, "_blank", "noopener,noreferrer");
    }
  };

  const reloadIframe = () => setIframeReloadKey((k) => k + 1);

  const downloadFile = (fileUrl: string, fileName: string) => {
    const anchor = document.createElement("a");
    anchor.href = fileUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleFileDownload = async (path: string) => {
    const fileName = path.split("\\");
    const response = await http.apiGetFiles({
      url: `${apiUrl.BOARD_CONTENT_NETWORK_FILE}?path=${encodeURIComponent(path)}`,
      config: {
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        method: "GET",
        responseType: "blob",
      },
    });
    if (!response?.data) return;
    const fileUrl: string = URL.createObjectURL(response.data);
    downloadFile(fileUrl, fileName[fileName.length - 1]);
  };

  const handleOnClickDetail = () => {
    if (!urlDetail) {
      if (attachmentId) setIsModalOpen(true);
      return;
    }
    if (urlDetail.includes("http")) {
      window.open(urlDetail, "_self");
      return;
    }
    if (isPreviewSupported) {
      setIsModalOpen(true);
    } else {
      handleFileDownload(urlDetail);
    }
  };

  const getModalTitle = () => {
    if (!urlDetail) return "Preview";
    const fileName = urlDetail.split("\\");
    return fileName[fileName.length - 1];
  };

  return (
    <Fragment key={boardContent.id}>
      <Card
        key={boardContent.id}
        ref={cardRef}
        style={{
          height: isFullScreen
            ? `calc((100vh - 95px) / ${board.rows})`
            : `calc((100vh - 170px) / ${board.rows})`,
          border: "1px solid gray",
          boxShadow: "1px #888888",
          margin: 5,
          padding: 0,
          backgroundColor:
            !attachmentId && !boardContent.urlPreview ? "#d0d0d0" : "#fff",
        }}
      >
        <CardHeader
          style={{
            height: "25px",
            minWidth: "100%",
            color: theme.palette.primary.main,
            borderBottom: "1px solid gray",
            paddingLeft: 5,
            whiteSpace: "nowrap",
            backgroundColor: "#fff",
          }}
          action={getAction()}
          title={
            <>
              <div
                style={{
                  display: "inline-flex",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    marginLeft: 5,
                    marginTop: "3px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span
                    title={
                      boardContent.headline ? boardContent.headline : undefined
                    }
                  >
                    {boardContent?.headline}
                  </span>
                </div>
              </div>
            </>
          }
        />
        <CardContent
          style={{
            textAlign: "center",
            padding: 0,
          }}
        >
          {/* je attachment */}
          {boardContent.id && attachmentId && (
            <div
              style={{
                height: attachmentHeight + "px",
                top: 0,
                background: `url("/api/${apiUrl.BOARD_CONTENT_FILE_ATTACHMENT_DOWNLOAD}?id=${attachmentId}")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "top",
                backgroundSize: "contain",
              }}
              onClick={handleOnClickDetail}
            />
          )}
          {/* není attachment, je urlPreview */}
          {boardContent.id && !attachmentId && boardContent.urlPreview && (
            <div
              style={{
                height: window.outerHeight * boardContent.urlPreviewZoom + "px",
                width: window.outerWidth * boardContent.urlPreviewZoom + "px",
                display: "inline-block",
                overflow: "hidden",
                position: "relative",
              }}
              onClick={() =>
                iframeState !== "failed" &&
                urlDetail &&
                window.open(urlDetail, "_self")
              }
            >
              <div
                style={{
                  position: "relative",
                  transform: `scale(${boardContent.urlPreviewZoom})`,
                  WebkitTransformOrigin: "0 0",
                  visibility:
                    iframeState === "failed" ? "hidden" : "visible",
                }}
              >
                <iframe
                  key={
                    boardContent.positionRow +
                    "_" +
                    boardContent.positionColumn +
                    "_" +
                    iframeReloadKey
                  }
                  width={
                    boardContent.urlPreviewWidth
                      ? boardContent.urlPreviewWidth
                      : 1920
                  }
                  height={
                    boardContent.urlPreviewHeight
                      ? boardContent.urlPreviewHeight
                      : 1080
                  }
                  src={boardContent.urlPreview}
                  name={boardContent.urlPreview}
                  title={boardContent.urlPreview}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-storage-access-by-user-activation"
                  referrerPolicy="no-referrer-when-downgrade"
                  allow="clipboard-read; clipboard-write"
                  onLoad={handleIframeLoad}
                ></iframe>
              </div>
              {iframeState !== "failed" && (
                <div
                  className="overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOnClickDetail();
                  }}
                ></div>
              )}
              {iframeState === "failed" && (
                <div
                  className="iframe-fallback"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="iframe-fallback-message">
                    Náhled se nepodařilo načíst. Cílová stránka pravděpodobně
                    nepovoluje vložení do iframu nebo vyžaduje SSO přihlášení.
                  </div>
                  <div className="iframe-fallback-actions">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={openIframeInNewTab}
                    >
                      Otevřít v novém okně
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={prewarmSso}
                    >
                      Přihlásit se v novém okně
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={reloadIframe}
                    >
                      Zkusit znovu
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* není attachment, není urlPreview */}
          {!attachmentId &&
          !boardContent.urlPreview &&
          urlDetail &&
          isPreviewSupported ? (
            <span onClick={handleOnClickDetail}>
              <BoardContentCardFilePreview
                fileName={getModalTitle()}
                path={urlDetail}
                attachmentHeight={attachmentHeight}
              />
            </span>
          ) : (
            !urlDetail && (
              <Skeleton
                sx={{
                  backgroundImage: `url(${placeholder})`,
                  backgroundSize: "100% 100%",
                  backgroundRepeat: "no-repeat !important",
                  backgroundPosition: "center center",
                }}
                animation={false}
                variant="rectangular"
                height={attachmentHeight + "px"}
                onClick={handleOnClickDetail}
              />
            )
          )}
        </CardContent>
      </Card>
      <CustomDialog
        open={isModalOpen}
        title={getModalTitle()}
        maxWidth={false}
        onClose={() => setIsModalOpen(false)}
      >
        {urlDetail && (
          <BoardContentCardFilePreview
            fileName={getModalTitle()}
            path={urlDetail}
          />
        )}
        {!urlDetail && attachmentId && (
          <div
            style={{
              minHeight: 80 + "vh",
              width: "auto",
              marginBottom: 0,
              top: 0,
              background: `url("/api/${apiUrl.BOARD_CONTENT_FILE_ATTACHMENT_DOWNLOAD}?id=${attachmentId}")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top",
              backgroundSize: "contain",
            }}
          />
        )}
      </CustomDialog>
    </Fragment>
  );
}
